import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Cloud, Save, Zap, CheckCircle2, Copy } from "lucide-react";

export default function AwsSettings() {
  const [form, setForm] = useState({
    access_key_id: "",
    secret_access_key: "",
    region: "us-east-1",
    iot_endpoint: "",
    policy_name: "QuantzillionIoTDevicePolicy",
  });
  const [configured, setConfigured] = useState(false);
  const [ruleInfo, setRuleInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [setupBusy, setSetupBusy] = useState(false);
  const [manualToken, setManualToken] = useState(null);

  const fetchToken = async () => {
    try {
      const { data } = await api.get("/aws/last-confirmation-token");
      setManualToken(data);
    } catch (_) { /* ignore */ }
  };

  const refresh = async () => {
    const { data } = await api.get("/aws/settings");
    setSettings(data);
    if (data?.configured) {
      setConfigured(true);
      setForm((f) => ({
        ...f,
        access_key_id: data.access_key_id || "",
        secret_access_key: "",
        region: data.region || "us-east-1",
        iot_endpoint: data.iot_endpoint || "",
        policy_name: data.policy_name || "QuantzillionIoTDevicePolicy",
      }));
    }
  };

  useEffect(() => { refresh(); fetchToken(); }, []);

  const save = async () => {
    const payload = { ...form };
    if (!payload.secret_access_key) delete payload.secret_access_key;
    await api.put("/aws/settings", payload);
    toast.success("AWS settings saved");
    setConfigured(true);
    refresh();
  };

  const setupRule = async () => {
    setSetupBusy(true);
    try {
      const { data } = await api.post("/aws/setup-mqtt-rule");
      setRuleInfo(data);
      toast.success("MQTT ingest rule created");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create rule");
    } finally {
      setSetupBusy(false);
    }
  };

  return (
    <div data-testid="aws-page">
      <PageHeader eyebrow="// CONNECTION" title="AWS IoT Core" description="Plug your AWS credentials in to enable real device provisioning and live MQTT ingestion." />
      <PageBody>
        <div className="max-w-3xl">
          {/* Credentials */}
          <div className="border border-neutral-200 bg-white p-6">
            <div className="flex items-start gap-3">
              <Cloud className="w-5 h-5 text-[#002FA7] mt-1" />
              <div>
                <div className="font-display text-xl font-semibold">{configured ? "Connection saved" : "Not connected yet"}</div>
                <p className="text-sm text-neutral-600 mt-1">
                  {configured
                    ? "Credentials are stored against your account. Update any field below to change them."
                    : "Until you connect, devices use our HTTPS ingest endpoint and AWS provisioning is unavailable."}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <Field label="AWS Access Key ID" testid="aws-access-key" value={form.access_key_id} onChange={(v) => setForm({ ...form, access_key_id: v })} placeholder="AKIAxxxxxxxxxxxx" />
              <Field label="AWS Secret Access Key" testid="aws-secret-key" type="password" value={form.secret_access_key} onChange={(v) => setForm({ ...form, secret_access_key: v })} placeholder={configured ? "•••• leave blank to keep" : ""} />
              <Field label="Region" testid="aws-region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} placeholder="us-east-1" />
              <Field label="IoT Endpoint" testid="aws-endpoint" value={form.iot_endpoint} onChange={(v) => setForm({ ...form, iot_endpoint: v })} placeholder="xxxxx-ats.iot.us-east-1.amazonaws.com" />
              <Field label="Device Policy Name" testid="aws-policy" value={form.policy_name} onChange={(v) => setForm({ ...form, policy_name: v })} placeholder="QuantzillionIoTDevicePolicy" />
            </div>

            <button onClick={save} data-testid="save-aws-btn" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#002FA7] text-white font-semibold hover:bg-[#001E70]">
              <Save className="w-4 h-4" /> Save credentials
            </button>
          </div>

          {/* MQTT ingest rule */}
          <div className="mt-6 border border-neutral-200 bg-white p-6">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-[#002FA7] mt-1" />
              <div className="flex-1">
                <div className="font-display text-xl font-semibold">MQTT ingest rule</div>
                <p className="text-sm text-neutral-600 mt-1">
                  Create an AWS IoT topic rule that forwards every MQTT message published to
                  <code className="mx-1 font-mono text-xs bg-neutral-100 px-1.5 py-0.5">devices/+/telemetry</code>
                  into your live dashboard.
                </p>
              </div>
              {settings?.rule_status === "active" && (
                <span className="inline-flex items-center gap-1 text-xs font-mono text-[#00A040]">
                  <CheckCircle2 className="w-4 h-4" /> active
                </span>
              )}
            </div>

            {settings?.rule_name && (
              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
                <div className="bg-white p-3">
                  <div className="label-eyebrow text-neutral-500">Rule</div>
                  <div className="font-mono text-xs mt-1 break-all">{settings.rule_name}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="label-eyebrow text-neutral-500">Webhook</div>
                  <div className="font-mono text-xs mt-1 break-all">{settings.webhook_url}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="label-eyebrow text-neutral-500">Status</div>
                  <div className="font-mono text-xs mt-1">{settings.rule_status}</div>
                </div>
              </dl>
            )}

            <button
              onClick={setupRule}
              disabled={!configured || setupBusy}
              data-testid="setup-mqtt-rule-btn"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-neutral-900 font-semibold hover:bg-neutral-900 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className="w-4 h-4" /> {settings?.rule_name ? "Re-check / refresh rule" : "Create MQTT ingest rule"}
            </button>

            {ruleInfo && (
              <pre className="mt-4 bg-neutral-50 border border-neutral-200 p-3 text-xs font-mono whitespace-pre-wrap break-all" data-testid="rule-info">
{JSON.stringify(ruleInfo, null, 2)}
              </pre>
            )}
          </div>

          {/* Manual confirmation helper */}
          <div className="mt-6 border border-neutral-200 bg-white p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow text-neutral-500">// MANUAL CONFIRMATION</div>
                <div className="font-display text-xl font-semibold mt-1">Latest confirmation token</div>
                <p className="text-sm text-neutral-600 mt-1 max-w-xl">
                  AWS console requires manual token entry when its auto-confirmation window expires.
                  Copy the latest token AWS sent to our backend and paste it into the AWS console&apos;s
                  <em> &quot;Confirm and activate&quot;</em> dialog.
                </p>
              </div>
              <button onClick={fetchToken} data-testid="refresh-token-btn" className="text-xs px-3 py-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white">
                Refresh
              </button>
            </div>

            {manualToken?.token ? (
              <>
                <textarea
                  readOnly
                  value={manualToken.token}
                  data-testid="manual-token-text"
                  className="mt-4 w-full font-mono text-[11px] border border-neutral-300 bg-neutral-50 p-3 h-32 resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-neutral-500 font-mono">
                    Received: {manualToken.received_at ? new Date(manualToken.received_at).toLocaleString() : "—"}
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(manualToken.token); toast.success("Token copied"); }}
                    data-testid="copy-token-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]"
                  >
                    <Copy className="w-4 h-4" /> Copy token
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-4 text-sm text-neutral-500">
                No token received yet. In the AWS console&apos;s <em>Confirm destination</em> dialog,
                click <strong>&quot;Send new token&quot;</strong>, then come back here and hit <strong>Refresh</strong>.
              </div>
            )}
          </div>

          {/* IAM */}
          <div className="mt-6 border border-neutral-200 bg-white p-6">
            <div className="label-eyebrow text-neutral-500">// IAM PERMISSIONS NEEDED</div>
            <ul className="mt-3 text-sm text-neutral-700 list-disc pl-5 space-y-1">
              <li><code className="font-mono">iot:CreateThing</code>, <code className="font-mono">iot:DescribeEndpoint</code></li>
              <li><code className="font-mono">iot:CreateKeysAndCertificate</code></li>
              <li><code className="font-mono">iot:CreatePolicy</code>, <code className="font-mono">iot:AttachPolicy</code>, <code className="font-mono">iot:AttachThingPrincipal</code></li>
              <li><code className="font-mono">iot:CreateTopicRule</code>, <code className="font-mono">iot:CreateTopicRuleDestination</code></li>
            </ul>
          </div>
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow text-neutral-500">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-sm font-mono text-sm" />
    </div>
  );
}
